// Ranking goals by importance and by urgency, and choosing where to start. Pure; tested beside this file.
import { urgentImportantGoals } from "../radar/signals";
import { sortGoalsForView, GOAL_VIEWS } from "../tasks/goalOrder";
import { addableTasks } from "../today/addToPhase";
import { tasksForGoal } from "../tasks/goalOrder";

export const RANK_FIELD = { [GOAL_VIEWS.IMPORTANCE]: "goalImportanceOrder", [GOAL_VIEWS.URGENCY]: "goalUrgencyOrder", [GOAL_VIEWS.PREFERENCE]: "goalPreferenceOrder" };

// What each ranking step says. Craig, 18 Oct 2026: keep the "Goals sorted by…" wording and say what the
// word means. Urgency's second sentence is Craig's (23 Oct 2026).
export const RANK_COPY = {
  [GOAL_VIEWS.IMPORTANCE]: {
    title: "Goals sorted by importance",
    body: "Importance is how much a goal matters to the life you want, whatever its deadline. Put the goal that would make the biggest difference at the top. Drag ⠿ to move a goal.",
  },
  [GOAL_VIEWS.URGENCY]: {
    title: "Goals sorted by urgency",
    body: "Urgency is how soon a goal needs you, because a deadline is close or waiting will cost you. Sort the goals that need action right away to the top. Drag ⠿ to move a goal.",
  },
  [GOAL_VIEWS.PREFERENCE]: {
    title: "Goals sorted by preference",
    body: "Preference is the order you'd work on your goals in if it were only up to how you feel. Put the goal you most look forward to at the top. Until you move one, your goals are in the same order as Importance. When a goal is high on Importance but low here, you may be putting it off. Drag ⠿ to move a goal.",
  },
};

// The writes a new order needs: rank 1, 2, 3… for each goal whose stored rank differs. A goal that was
// never ranked gets one too, so the order you see is the order that is kept.
export const rankChanges = (view, ordered) => {
  const field = RANK_FIELD[view];
  return ordered.map((g, n) => ({ id: g.id, field, rank: n + 1, was: g[field] ?? null })).filter(c => c.rank !== c.was);
};

// The order to show while a ranking step is open: the user's order, without goals that have gone, with any
// new goal added at the end.
export const mergeOrder = (order, liveIds) => [...order.filter(id => liveIds.includes(id)), ...liveIds.filter(id => !order.includes(id))];

// The goals to start from: those near the top of both lists, then by importance, with up to `perGoal`
// tasks each that can go in this part of the day. Each says where it ranks.
export const startHere = (tasks, goals, phaseId, { goalCount = 2, perGoal = 2 } = {}) => {
  const active = (goals || []).filter(g => g.status === "active");
  const open = addableTasks(tasks, phaseId);
  const byBoth = urgentImportantGoals(active).map(r => ({ goal: r.goal, importance: r.i, urgency: r.u }));
  const importanceRank = new Map(sortGoalsForView(active, GOAL_VIEWS.IMPORTANCE).map((g, n) => [g.id, n + 1]));
  const byImportance = sortGoalsForView(active, GOAL_VIEWS.IMPORTANCE).map(g => ({ goal: g, importance: importanceRank.get(g.id), urgency: null }));
  const seen = new Set();
  return [...byBoth, ...byImportance]
    .filter(r => !seen.has(r.goal.id) && seen.add(r.goal.id))
    .map(r => ({ ...r, tasks: tasksForGoal(open, r.goal.id).slice(0, perGoal) }))
    .filter(r => r.tasks.length > 0)
    .slice(0, goalCount);
};

// "#3 important · #1 urgent", or just the importance when the goal has no urgency rank.
export const rankBadge = ({ importance, urgency }) =>
  [importance != null && `#${importance} important`, urgency != null && `#${urgency} urgent`].filter(Boolean).join(" · ");
